import bbox from '@turf/bbox';
import { featureCollection, point } from '@turf/helpers';

/**
 * Utility function to calculate bounding box (bbox) for a set of coordinates
 * @param coordinates Array of coordinates {lng: number, lat: number}[]
 * @returns Bounding box as [minX, minY, maxX, maxY]
 */
function getBoundingBox(coordinates: { lng: number; lat: number }[]): [number, number, number, number] {
  // TODO don't show tracker without position
  coordinates = coordinates.filter(coord => coord.lng !== null && coord.lat !== null);

  const points = coordinates.map(({ lng, lat }) => point([lng, lat]));
  const features = featureCollection(points);

  // Get the bounding box, and ensure it is 2D by slicing the first 4 elements
  const boundingBox = bbox(features).slice(0, 4) as [number, number, number, number];

  return boundingBox;
}

/**
 * Converts a bbox to a tuple of LngLatLike
 * @param bbox Bounding box in [west, south, east, north] order
 * @returns A tuple of LngLatLike in [southWest, northEast] order
 */
function convertBboxToLngLatBoundsLike(bbox: [number, number, number, number]): [[number, number], [number, number]] {
  const [west, south, east, north] = bbox;

  // Return a tuple of LngLatLike in [southWest, northEast] format
  return [
    [west, south],
    [east, north],
  ];
}

/**
 * Utility function to get fit bounds for a set of coordinates
 * @param coordinates Array of coordinates {lng: number; lat: number}[]
 * @returns A tuple of LngLatLike [southWest, northEast] that can be used with map.fitBounds()
 */
export function getFitBounds(coordinates: { lng: number; lat: number }[]): [[number, number], [number, number]] {
  const boundingBox = getBoundingBox(coordinates);

  // Convert bbox to LngLatLike tuple and return
  return convertBboxToLngLatBoundsLike(boundingBox);
}

/**
 * Calculates dynamic padding based on the bounding box size.
 * @param fitBounds Bounding box as a tuple of LngLatLike [southWest, northEast]
 * @returns Calculated padding value
 */
export function calculateDynamicPadding(fitBounds: [[number, number], [number, number]]): number {
  const [[west, south], [east, north]] = fitBounds;

  // Calculate the approximate width and height of the bounding box
  const width = Math.abs(east - west); // Longitude difference
  const height = Math.abs(north - south); // Latitude difference

  // Use the larger dimension to calculate padding
  const maxDimension = Math.max(width, height);

  // Dynamically set padding: larger bounding boxes get smaller padding, smaller bounding boxes get larger padding
  // You can tweak the formula based on your needs
  const padding = Math.max(50, Math.min(200, maxDimension * 1000)); // Adjust 1000 factor to scale padding
  return padding;
}
